import { useAnalytics } from 'app/context/Analytics';

import { PosthogEventName } from '../../../copied/constants/PosthogEventName';
import { SuggestedEntry } from '../../../copied/models/ChatModules';
import { Category, FooterLink, ModuleItem, ModuleItemActionType } from '../../../services/helpCenter';
import { getTopicType } from '../../../services/search';
import useLinks from './useLinks';

export default function useTailorTracking() {
  const { shouldShowInterstitial } = useLinks();
  const { captureEventWithTopicSourceAndActiveScreen, captureEventWithActiveScreen } = useAnalytics();

  const captureSearchResultClick = (
    result: SuggestedEntry,
    { resultIndex, search, categoryTitle }: { resultIndex: number; search: string; categoryTitle?: string }
  ) => {
    const isDownload = !!result.resource_link;
    const isExternalArticle = !!result.external_link;
    const isInternalArticle = !isExternalArticle && !isDownload;

    const URLToOpen = isDownload ? result.resource_link : result.external_link;

    let eventName: PosthogEventName = PosthogEventName.TopicScreenTopicClicked;
    if (isInternalArticle) {
      eventName = PosthogEventName.TopicScreenTopicClicked;
    } else if (isDownload) {
      eventName = PosthogEventName.ResourceDownloaded;
    }

    const linkUrl = isDownload ? result.resource_link : isExternalArticle ? result.external_link : undefined;

    captureEventWithTopicSourceAndActiveScreen(eventName, {
      resultIndex,
      topicType: getTopicType(result),
      topicTitle: result.title,
      queryTyped: search,
      ...(isDownload && { isFlow: false }),
      ...(linkUrl && { linkUrl }),
      ...(categoryTitle && { categoryTitle }),
      toInterstitial: shouldShowInterstitial(URLToOpen),
      searchSnippet: !!result.highlight_tag_text,
      searchSnippetdisplay: result.highlight_tag_text || '',
    });
  };

  const captureArticleLinkClick = ({
    conditionId,
    search,
    title,
    href,
    type,
    ctaCopy,
  }: {
    conditionId: string;
    search: string;
    title: string;
    href?: string;
    type: 'link' | 'button';
    ctaCopy?: string | null;
  }) => {
    captureEventWithTopicSourceAndActiveScreen(
      type === 'link' ? PosthogEventName.TopicScreenLinkClicked : PosthogEventName.TopicScreenButtonClicked,
      {
        conditionUUID: conditionId,
        queryTyped: search,
        topicTitle: title,
        isFlow: false,
        linkUrl: href,
        ctaCopy,
        toInterstitial: !!href && shouldShowInterstitial(href),
      }
    );
  };

  const captureArticlePageview = (
    article: SuggestedEntry,
    {
      conditionId,
      search,
      isSuggested,
    }: {
      conditionId: string;
      search: string;
      isSuggested: boolean;
    }
  ) => {
    captureEventWithTopicSourceAndActiveScreen(PosthogEventName.TopicScreenView, {
      conditionUUID: conditionId,
      isFlow: false,
      queryTyped: search,
      topicTitle: article.title,
      isSuggested,
    });
  };

  const captureFooterLinkClick = (footerLink?: FooterLink) => {
    captureEventWithActiveScreen(PosthogEventName.ContactClicked, {
      link: footerLink?.link,
      text: footerLink?.text,
      toInterstitial: !!footerLink?.link && shouldShowInterstitial(footerLink?.link),
    });
  };

  const captureLandingPageSearchInputClicked = () => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageSearchInputClicked);
  };

  const captureLandingPageCTAClicked = (button: ModuleItem) => {
    if (button.action_obj?.action_type === ModuleItemActionType.OPEN_VIRTUAL_ASSISTANT) {
      captureEventWithActiveScreen(PosthogEventName.LandingPageVirtualChatClicked, {
        topicType: 'VirtualChatCTA',
        topicTitle: button.title,
      });
    } else {
      captureEventWithActiveScreen(PosthogEventName.LandingPageCTAClicked, {
        ctaCopy: button.title,
        linkPath: button.action_obj.url,
        toInterstitial: !!button.action_obj.url && shouldShowInterstitial(button.action_obj.url),
      });
    }
  };

  const captureLandingPagePageview = ({ autoOpen, autoopenDelay }: { autoOpen: boolean; autoopenDelay: number }) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageView, {
      autoOpen,
      autoopenDelay,
    });
  };

  const captureLandingPageCategoryClicked = (
    category: Category,
    { resultIndex, halfAutoOpen }: { resultIndex: number; halfAutoOpen: boolean }
  ) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageCategoryClicked, {
      categoryTitle: category.name,
      resultIndex,
      halfAutoOpen,
    });
  };

  const captureLandingPageLinkClicked = (linkUrl: string) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageLinkClicked, {
      linkUrl,
    });
  };

  const captureISILinkClick = ({ linkUrl }: { linkUrl?: string }) => {
    captureEventWithActiveScreen(PosthogEventName.ISIClicked, {
      linkUrl,
    });
  };

  const captureTOULinkClick = () => {
    captureEventWithActiveScreen(PosthogEventName.ToUClicked);
  };

  const capturePPCLick = () => {
    captureEventWithActiveScreen(PosthogEventName.PPClicked);
  };

  const captureLanguageSwitch = (
    flowID: string,
    languageSelected: string | null,
    previousLanguage: string | null
  ): void => {
    captureEventWithActiveScreen(PosthogEventName.LanguageSelectorClicked, {
      flowID,
      languageSelected,
      previousLanguage,
    });
  };

  const captureLandingPageVAClicked = ({
    topicType,
    topicTitle,
  }: {
    topicType: 'VirtualChatCTA' | 'VirtualChatTopic';
    topicTitle: string;
  }) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageVirtualChatClicked, {
      topicType,
      topicTitle,
    });
  };

  const captureLandingPageLogoClicked = (link: string) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageLogoClicked, {
      link,
      toInterstitial: !!link && shouldShowInterstitial(link),
    });
  };

  const captureTailorMinimized = (halfautoOpen: boolean) => {
    captureEventWithActiveScreen(PosthogEventName.Minimized, { halfautoOpen });
  };

  const captureTailorExpanded = (halfautoOpen: boolean) => {
    captureEventWithActiveScreen(PosthogEventName.Expanded, { halfautoOpen });
  };

  const captureInterstitialClick = (link: string, interstitialType: 'general' | 'specific') => {
    captureEventWithActiveScreen(PosthogEventName.SearchInterstitialCTAClicked, {
      link,
      interstitialType,
    });
  };

  const captureHalfOpen = (halfautoOpen: boolean, autoopenDelay: number) => {
    captureEventWithActiveScreen(PosthogEventName.HalfOpen, {
      halfautoOpen,
      autoopenDelay,
    });
  };

  const captureScrollProgress = (progress: number) => {
    captureEventWithActiveScreen(PosthogEventName.ScrollDepth, {
      depth: progress,
    });
  };

  const captureLandingPageLiveChatClicked = () => {
    captureEventWithActiveScreen(PosthogEventName.LandingPageLiveChatClicked);
  };

  const captureLandingPagePillClicked = (pill: ModuleItem) => {
    captureEventWithActiveScreen(PosthogEventName.LandingPagePillClicked, {
      optionLabel: pill.pill_text,
      pillType: pill.pill_type,
    });
  };

  return {
    captureLandingPageSearchInputClicked,
    captureLandingPageCategoryClicked,
    captureLandingPageLinkClicked,
    captureLandingPageCTAClicked,
    captureLandingPagePageview,
    captureSearchResultClick,
    captureArticleLinkClick,
    captureArticlePageview,
    captureFooterLinkClick,
    captureISILinkClick,
    captureTOULinkClick,
    capturePPCLick,
    captureLanguageSwitch,
    captureLandingPageVAClicked,
    captureLandingPageLogoClicked,
    captureTailorMinimized,
    captureTailorExpanded,
    captureInterstitialClick,
    captureHalfOpen,
    captureScrollProgress,
    captureLandingPageLiveChatClicked,
    captureLandingPagePillClicked,
  };
}
